import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";

import * as api from "./trendAlertsApi";
import { onStateFulfilled, onStatePending, onStateRejected } from "../utils";

export const trendAlertsAdapter = createEntityAdapter<
  TrendAlert.Data | TrendAlert.CreationData
>({
  sortComparer: (a, b) => a.createdAt.localeCompare(b.createdAt),
});

const initialState = trendAlertsAdapter.getInitialState<
  Store.InitialState & {
    recentIds: TrendAlert.Data["id"][];
    hasLoadedAll: boolean;
    lastVisibleDoc: string | null;
    currentVolumes: Record<string, number>;
    loadingCurrentVolumes: Record<string, boolean>;
  }
>({
  status: "idle",
  error: null,
  recentIds: [],
  hasLoadedAll: false,
  lastVisibleDoc: null,
  currentVolumes: {},
  loadingCurrentVolumes: {},
});

export const fetchTrendAlerts = createAsyncThunk<
  TrendAlert.Data[],
  {
    authorId: string;
    trackerId?: Tracker.Data["id"];
    skipGlobalLoader?: boolean;
  }
>("trendAlerts/fetch", async ({ authorId, trackerId }) => {
  return api.getTrendAlerts(authorId, trackerId);
});

export const createTrendAlert = createAsyncThunk<
  TrendAlert.Data | null,
  TrendAlert.CreationData
>("trendAlerts/create", async (payload) => {
  return api.createTrendAlert(payload);
});

export const updateTrendAlert = createAsyncThunk<
  TrendAlert.Data | null,
  TrendAlert.CreationData
>("trendAlerts/update", async (payload) => {
  return api.updateTrendAlert(payload);
});

export const deleteTrendAlert = createAsyncThunk<
  TrendAlert.Data["id"],
  TrendAlert.Data["id"]
>("trendAlerts/delete", async (payload) => {
  await api.deleteTrendAlert(payload);

  return payload;
});

export const getCurrentVolume = createAsyncThunk<
  { trackerId: string; volume: number },
  string
>("trendAlerts/getCurrentVolume", async (trackerId) => {
  const volume = await api.getCurrentVolume(trackerId);
  return { trackerId, volume };
});

const trendAlertsSlice = createSlice({
  name: "trendAlerts",
  initialState,
  reducers: {
    setCurrentVolumeFromAlert: (
      state,
      action: { payload: { trackerId: string; volume: number } },
    ) => {
      const { trackerId, volume } = action.payload;
      state.currentVolumes[trackerId] = volume;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrendAlerts.pending, onStatePending)
      .addCase(fetchTrendAlerts.rejected, onStateRejected)
      .addCase(fetchTrendAlerts.fulfilled, (state, action) => {
        trendAlertsAdapter.upsertMany(state, action.payload);
        onStateFulfilled(state, action);
        state.hasLoadedAll = true;
      })

      .addCase(createTrendAlert.fulfilled, (state, action) => {
        if (action.payload) {
          trendAlertsAdapter.addOne(state, action.payload);
        }
      })

      .addCase(updateTrendAlert.fulfilled, (state, action) => {
        if (action.payload) {
          trendAlertsAdapter.updateOne(state, {
            id: action.payload.id,
            changes: action.payload,
          });
        }
      })

      .addCase(deleteTrendAlert.fulfilled, trendAlertsAdapter.removeOne)

      .addCase(getCurrentVolume.pending, (state, action) => {
        const trackerId = action.meta.arg;
        state.loadingCurrentVolumes[trackerId] = true;
      })
      .addCase(getCurrentVolume.fulfilled, (state, action) => {
        const { trackerId, volume } = action.payload;
        state.currentVolumes[trackerId] = volume;
        state.loadingCurrentVolumes[trackerId] = false;
      })
      .addCase(getCurrentVolume.rejected, (state, action) => {
        const trackerId = action.meta.arg;
        state.loadingCurrentVolumes[trackerId] = false;
      });
  },
});

export const { setCurrentVolumeFromAlert } = trendAlertsSlice.actions;

export default trendAlertsSlice.reducer;
